import Box from 'UIKit/Box/Box'
import Image from 'UIKit/Image'
import { AirdropBox } from 'config/types'
import { useAsyncConfirmation } from 'hooks/useAsyncConfirmation'
import useMatchBreakpoints from 'hooks/useMatchBreakpoints'
import { useVariableLoadedListener } from 'hooks/useVariableLoadedListener'
import { useEffect, useRef, useState } from 'react'
import { useAirdropBoxStorage, useRedeemAirdrop } from 'state/bonus/hooks'
import { useUserSettings } from 'state/profile/hooks'
import styled, { css } from 'styled-components'
import { delayed } from 'utils'
import { AirdropGiftAnimation, buildFx } from 'utils/airdrop'
import { SentryHelper } from 'utils/sentryHelper'
import ClaimAirdropFirstTime from './components/ClaimAirdropFirstTime'
import { useIncomingBoxes, useNotificationAirdropDesktop, useNotificationAirdropMobile } from './hooks'

const AirdropAnimation = () => {
  const [isRunning, setIsRunning] = useState(false)
  const [isClaiming, setIsClaiming] = useState(false)
  const { userSettings } = useUserSettings()

  const { droppingBox, receivedBox } = useIncomingBoxes()
  const { isDesktop } = useMatchBreakpoints()

  const { pushNewBox } = useAirdropBoxStorage()
  const redeemAirdrop = useRedeemAirdrop()
  const pushNotification = useNotificationAirdropDesktop()
  const pushNotificationMobile = useNotificationAirdropMobile()

  const { confirm, onConfirming } = useAsyncConfirmation()
  const animatedElementRef = useRef<HTMLDivElement>(null)
  const animationHandler = useRef<AirdropGiftAnimation>(null)

  const isLoaded = useVariableLoadedListener(
    () =>
      document.getElementById('airdrop-husd-logo') &&
      document.getElementById('airdrop-gift-box') &&
      animatedElementRef.current,
  )

  useEffect(() => {
    return () => {
      killAnimation()
    }
  }, [])

  useEffect(() => {
    if (droppingBox) {
      onStart()
    }
  }, [droppingBox])

  const killAnimation = () => {
    if (animationHandler.current) {
      animationHandler.current.kill()
      animationHandler.current = null
    }
  }

  const onInitAnimation = () => {
    if (isLoaded) {
      killAnimation()
      animationHandler.current = new AirdropGiftAnimation(
        animatedElementRef.current,
        document.getElementById('airdrop-husd-logo'),
        document.getElementById('airdrop-gift-box'),
        buildFx(),
      )
      animationHandler.current?.fitPosition()
      return true
    }

    return false
  }

  const onStart = async () => {
    const result = onInitAnimation()

    if (!result) {
      SentryHelper.captureFeatureClientError({
        feature: 'HUSD Airdrop Box',
        error: new Error('Failed init airdrop animation'),
      })
      return
    }

    if (userSettings.isDisableAnimationAirdrop) {
      pushNewBox(droppingBox)
      receivedBox()
    } else {
      setIsRunning(true)
      animationHandler.current.run(
        async (instance) => {
          const preTransition = animatedElementRef.current.style.transition
          animatedElementRef.current.style.transition = 'transform 0.2s 0.1s'
          animatedElementRef.current.style.transform = `rotate(${instance.currentPoint.angleRadian}deg)`
          // waiting animation
          await delayed(300)
          if (animatedElementRef.current) animatedElementRef.current.style.transition = preTransition
        },
        () => {
          if (animatedElementRef.current) animatedElementRef.current.style.transform = 'rotate(0deg)'
          pushNewBox(droppingBox)
          setIsRunning(false)
          receivedBox()
        },
      )
    }
  }

  const completeReceiveAirdrop = async () => {
    const preTransition = animatedElementRef.current.style.transition
    animatedElementRef.current.style.transition = 'transform 0.5s'
    animatedElementRef.current.style.transform = 'translate(0px,0px) scale(0.1)'
    await delayed(510)
    animatedElementRef.current.style.transition = preTransition
    setIsClaiming(false)
    setIsRunning(false)
    receivedBox()
  }

  const handleRedeemBonus = async () => {
    if (isClaiming) {
      return
    }

    setIsClaiming(true)
    const currentPoint = animationHandler.current.currentPoint

    if (!currentPoint) {
      return
    }

    animationHandler.current?.pause()
    animatedElementRef.current.style.transform = `translate(${currentPoint.x}px, ${currentPoint.y}px) rotate(${0}deg)`

    await redeemAirdrop(
      [droppingBox],
      async () => {
        // waiting animation
        await delayed(500)
        if (droppingBox?.isFirstTime) {
          await onConfirming()
        }
        completeReceiveAirdrop()
      },
      (boxes: AirdropBox[]) => {
        if (isDesktop) {
          pushNotification(boxes)
          return
        }
        pushNotificationMobile(boxes)
      },
    )
  }

  return (
    <StyledAirdrop
      ref={animatedElementRef}
      zIndex="110"
      width="0px"
      height="0px"
      position="absolute"
      $isRunning={isRunning}
    >
      <Box height="24px" ml="-12px" mt="-12px" width="24px" position="relative">
        {!isClaiming ? (
          <StyledAirdropParachute
            onClick={() => {
              handleRedeemBonus()
            }}
          >
            <Image width={150} height={150} src="/images/airdrop/airdrop-parachute.png" />
          </StyledAirdropParachute>
        ) : (
          <StyledGiftBoxContainer>
            <StyledGiftBoxDisplayFadeOut />
            <StyledGiftBoxWrapper
              position="absolute"
              top={0}
              left={0}
              right={0}
              bottom={0}
              $transitionDuration={animatedElementRef.current.style.transitionDuration}
              onMouseEnter={() => !isClaiming && animationHandler.current?.pause()}
            >
              <Box position="relative">
                {droppingBox?.isFirstTime && <ClaimAirdropFirstTime confirm={confirm} delayDisplay={1000} />}
                <Image width={50} height={50} src="/images/airdrop/airdrop-gift-box.png" />
              </Box>
            </StyledGiftBoxWrapper>
          </StyledGiftBoxContainer>
        )}
      </Box>
    </StyledAirdrop>
  )
}

const StyledAirdrop = styled(Box)<{ $isRunning?: boolean }>`
  pointer-events: none;
  visibility: hidden;

  ${({ $isRunning }) =>
    $isRunning &&
    css`
      pointer-events: unset !important;
      visibility: visible !important;
      ${StyledAirdropParachute} {
        animation: zoomOut 0.4s linear;
      }
    `}

  @keyframes zoomOut {
    0% {
      transform: translate(-50%) scale(0.4);
      bottom: -22px;
    }
    40% {
      bottom: -12px;
    }
    60% {
      transform: translateX(-50%) scale(1.2);
    }

    100% {
      transform: translateX(-50%) scale(1);
    }
  }
`
const StyledAirdropParachute = styled(Box)`
  width: 150px;
  position: absolute;
  bottom: -12px;
  left: 50%;
  transform: translateX(-50%);

  * {
    pointer-events: none;
  }
  transform-origin: center bottom;
`

const StyledGiftBoxContainer = styled(Box)`
  width: 50px;
  height: 50px;
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
`

const StyledGiftBoxDisplayFadeOut = styled(Box)`
  position: absolute;
  z-index: 2;
  width: 50px;
  height: 50px;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  transform-origin: center center;
  opacity: 1;
  border-radius: 50%;

  &::after {
    content: ' ';
    position: absolute;
    width: 100%;
    height: 100%;
    background: transparent;
    border: 2px solid #cecdcd;
    border-radius: 50%;
    opacity: 0;
    animation: pulse-ring 0.5s linear forwards;

    @keyframes pulse-ring {
      0% {
        transform: scale(0.6);
      }
      50% {
        opacity: 1;
        transform: scale(1.4);
      }
      100% {
        opacity: 0;
        display: none;
      }
    }
  }
`

const StyledGiftBoxWrapper = styled(Box)<{ $transitionDuration: string }>`
  position: absolute;
  width: 50px;
  height: 50px;
  left: 50%;
  top: 50%;
  opacity: 0;
  transform-origin: center center;
  transform: translate(-50%, -50%);

  @keyframes fadeOut {
    0% {
      opacity: 0;
      transform: translate(-50%, -50%);
    }

    50% {
      opacity: 1;
      transform: translate(-50%, -50%) scale(1.5);
    }

    100% {
      opacity: 1;
      transform: translate(-50%, -50%) scale(1);
    }
  }

  animation: fadeOut 0.5s linear forwards;
  animation-delay: 0.5s;

  &.back-to-start-animation {
    @keyframes zoomOutAndZoomIn {
      30% {
        transform: scale(1.4);
      }

      40% {
        transform: scale(1);
      }
      100% {
        transform: scale(0.1);
      }
    }

    ${({ $transitionDuration }) => css`
      animation: zoomOutAndZoomIn ${$transitionDuration} linear forwards;
    `}
  }
`

export default AirdropAnimation
